import React, { useState, useEffect } from "react";
import { visualStyles } from "../../../data/styleOptions";

/**
 * Style Dropdown Component
 * A reusable dropdown selector for visual styles with images and descriptions
 * Can be used in any form or control panel
 * 
 * Props:
 * - selectedStyleId: Select by ID (e.g., "digital-scifi")
 * - selectedStyleValue: Select by value (e.g., "Digital SciFi Art")
 * - onStyleChange: Callback with selected ID
 * - onStyleValueChange: Callback with selected value
 */
export default function StyleDropdown({
  selectedStyleId = null,
  selectedStyleValue = null,
  onStyleChange = (styleId) => {},
  onStyleValueChange = (styleValue) => {},
  label = "Visual Style",
  showDescription = true,
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('[data-style-dropdown]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Support both ID and value-based selection
  const selectedStyle = selectedStyleId 
    ? visualStyles.find((s) => s.id === selectedStyleId)
    : visualStyles.find((s) => s.value === selectedStyleValue);

  return (
    <div className="relative" data-style-dropdown>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm flex items-center justify-between"
      >
        <span>
          {selectedStyle?.title || "Select a style"}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-96 overflow-y-auto">
          {visualStyles.map((style) => (
            <button
              key={style.id}
              onClick={() => {
                onStyleChange(style.id);
                onStyleValueChange(style.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left border-b border-gray-200 last:border-b-0 hover:bg-indigo-50 flex gap-3 items-start transition-colors ${
                (selectedStyleId === style.id || selectedStyleValue === style.value) ? 'bg-indigo-50' : ''
              }`}
            >
              <img
                src={style.image}
                alt={style.title}
                className="w-16 h-12 object-cover rounded flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">{style.title}</p>
                {showDescription && (
                  <p className="text-xs text-gray-600 mt-0.5">{style.description}</p>
                )}
              </div>
              {(selectedStyleId === style.id || selectedStyleValue === style.value) && (
                <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
